/**
 * How Memory's internal records read to a person — shared by the Conflicts
 * tab and the Recent learnings feed so the two word the same source, key and
 * value the same way (FRONT-516, FRONT-2486).
 */

/** Turn an internal attribute key (`official_cash_rate`) into a readable phrase
 *  (`official cash rate`). A trailing run of 6+ digits is an id stamped on by
 *  whoever wrote the key, not part of its meaning, so it goes too. */
export function humanizeKey(key: string): string {
  return key.replace(/[_-]\d{6,}$/, '').replace(/[_-]+/g, ' ').trim() || key;
}

/** Same, capitalised, for the start of a line. */
export function keyLabel(key: string): string {
  const h = humanizeKey(key);
  return h.charAt(0).toUpperCase() + h.slice(1);
}

/** Friendly phrase for a raw write-source token, used mid-sentence ("Surfaced because …"). */
export function friendlySource(source: string | undefined): string {
  const s = (source || '').trim().toLowerCase();
  if (!s) return 'an unrecorded source';
  if (s === 'chat' || s === 'conversation') return 'a chat message';
  if (s === 'document' || s === 'file') return 'a document';
  if (s === 'web_search' || s === 'web search' || s === 'search') return 'a web search';
  if (s === 'reflect' || s === 'reflection') return 'a reflection';
  if (s === 'onboarding') return 'onboarding';
  if (s === 'observed') return 'something EDDY observed';
  if (s === 'user' || s === 'told by user' || s === 'stated by user') return 'something you told EDDY';
  if (s === 'user-resolved') return 'an earlier resolution';
  // `api` is the Memory page's own editor route; `system` is EDDY's bookkeeping.
  if (s === 'api') return 'an edit in the app';
  if (s === 'system') return 'EDDY itself';
  return humanizeKey(s);
}

/** Short source label, for a byline ("10:48 — told by you") or a chip. */
export function sourceLabel(source: string | undefined): string {
  const s = (source || '').trim().toLowerCase();
  if (!s || s === 'unknown') return 'source unknown';
  if (s === 'user' || s === 'told by user' || s === 'stated by user') return 'told by you';
  if (s === 'api') return 'added in the app';
  if (s === 'system') return 'added by EDDY';
  if (s === 'observed') return 'observed by EDDY';
  return `from ${friendlySource(s)}`;
}

/** A duplicate-entity conflict stores each side as "Name (<uuid>)" so the
 *  server can find the entity again; the id is for the server, not the reader. */
const TRAILING_ID = /\s*\([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\)\s*$/i;
export function displayConflictValue(value: string): string {
  return value.replace(TRAILING_ID, '') || value;
}

export interface ConflictLike {
  entity_name: string;
  category: string;
  key: string;
  source_a?: string;
  source_b?: string;
}

export function isEntityDuplicate(conflict: Pick<ConflictLike, 'category' | 'key'>): boolean {
  return conflict.category === '_entity' && conflict.key === 'duplicate_of';
}

/**
 * FRONT-516: a plain-language sentence describing what disagrees, replacing the
 * raw `category · key` machine string.
 */
export function describeConflict(conflict: ConflictLike): string {
  if (isEntityDuplicate(conflict)) {
    return `EDDY thinks “${conflict.entity_name}” might already exist as another entity.`;
  }
  return `Two facts about ${conflict.entity_name}’s ${humanizeKey(conflict.key)} disagree.`;
}

/**
 * FRONT-516: short "why this surfaced" line. FRONT-2486: when both sides came
 * from the same source, naming it twice read as a riddle ("something you told
 * EDDY mentioned an entity … from something you told EDDY"), so it is named once.
 */
export function conflictWhy(conflict: ConflictLike): string {
  const b = friendlySource(conflict.source_b);
  const a = friendlySource(conflict.source_a);
  const same = a === b;
  if (isEntityDuplicate(conflict)) {
    return same
      ? `Both names come from ${b}, and they are close enough to be the same thing.`
      : `Surfaced because ${b} mentioned an entity that looks like one EDDY already has from ${a}.`;
  }
  return same
    ? `Both values come from ${b}, and the newer one disagrees with the earlier one.`
    : `Surfaced because ${b} brought a new value that disagrees with what EDDY had stored from ${a}.`;
}
